import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Image from '../../../components/AppImage';
import Button from '../../../components/UI/Button';
import Icon from '../../../components/AppIcon';
import WaveDivider from './WaveDivider';
import { FeaturedProject, PersonalInfo } from '../types';

interface FeaturedProjectsProps {
  projects: FeaturedProject[];
  personalInfo: PersonalInfo;
}

const FeaturedProjects = ({ projects, personalInfo }: FeaturedProjectsProps) => {
  return (
    <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16 text-center">
      {/* Section Header */}
      <div className="mb-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="inline-flex items-center gap-3 px-4 py-2 bg-[#0D9488]/10 border border-[#0D9488]/20 rounded-full mb-8"
        >
          <span className="w-2 h-2 rounded-full bg-[#0D9488] animate-pulse" />
          <span className="text-[10px] font-black text-[#0D9488] uppercase tracking-[0.2em]">{personalInfo.projectsBadge || "Strategic Portfolio"}</span>
        </motion.div>

        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="font-inter text-5xl md:text-7xl font-black text-white mb-8 tracking-tighter leading-[0.9] uppercase"
        >
          {personalInfo.projectsTitle || "Featured Work"}
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="text-lg md:text-xl text-white/70 max-w-4xl mx-auto font-medium leading-relaxed mb-16"
        >
          {personalInfo.projectsSubtitle || "A curated selection of high-impact engineering projects, focusing on scalable architecture, clean code, and exceptional user experiences."}
        </motion.p>
      </div>

      {/* Projects Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
        {projects.map((project, index) => (
          <motion.div
            key={project.id}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.1 }}
            className="group neubrutal-card bg-white rounded-[2rem] overflow-hidden flex flex-col text-left"
          >
            {/* Project Image */}
            <div className="relative aspect-video overflow-hidden border-b-2 border-[#0C2C39]/5">
              <Image
                src={project.image}
                alt={project.imageAlt}
                className="w-full h-full object-cover transition-all duration-700 scale-105 group-hover:scale-100"
              />

              {/* Fixed Overlay Buttons */}
              <div className="absolute top-6 right-6 flex gap-3 opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-500">
                {project.liveUrl && (
                  <a
                    href={project.liveUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-4 bg-white text-[#0C2C39] border-2 border-[#0C2C39] shadow-[2px_2px_0px_0px_#0C2C39] hover:shadow-none transition-all duration-300"
                  >
                    <Icon name="ArrowUpRight" size={18} />
                  </a>
                )}
                {project.githubUrl && (
                  <a
                    href={project.githubUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-4 bg-[#0C2C39] text-white border-2 border-[#0C2C39] shadow-[2px_2px_0px_0px_#0C2C39] hover:shadow-none transition-all duration-300"
                  >
                    <Icon name="Github" size={18} />
                  </a>
                )}
              </div>
            </div>

            {/* Project Content */}
            <div className="p-10 flex flex-col flex-grow">
              <div className="flex flex-col mb-6">
                <span className="text-[10px] font-black uppercase tracking-[0.3em] text-[#0C2C39]/40 mb-2">
                  Technical Solution No. {String(index + 1).padStart(2, '0')}
                </span>
                <h3 className="font-inter text-3xl font-black text-[#0C2C39] tracking-tighter uppercase group-hover:text-teal-700 transition-colors leading-[0.9]">
                  {project.title}
                </h3>
              </div>

              <p className="text-[#0C2C39]/60 mb-8 leading-relaxed font-medium line-clamp-3">
                {project.description}
              </p>

              {/* Technologies - Neubrutal Badge Style */}
              <div className="mt-auto flex flex-wrap gap-2 mb-8">
                {project.technologies.slice(0, 4).map((tech, i) => (
                  <span key={i} className="px-3 py-1 bg-white text-[#0C2C39] text-[9px] font-black uppercase tracking-wider rounded-sm border-2 border-[#0C2C39]">
                    {tech}
                  </span>
                ))}
                {project.technologies.length > 4 && (
                  <span className="text-[10px] font-black text-[#0C2C39]/40 uppercase tracking-widest self-center ml-1">
                    +{project.technologies.length - 4} More
                  </span>
                )}
              </div>

              {/* Demo Credentials */}
              {project.demoCredentials && (project.demoCredentials.user || project.demoCredentials.password) && (
                <div className="mb-10 p-5 bg-[#0C2C39]/5 border-2 border-dashed border-[#0C2C39]/10 rounded-xl">
                  <div className="flex items-center gap-2 mb-4 text-[#0C2C39]/60 font-black text-[9px] uppercase tracking-[0.2em]">
                    <Icon name="Lock" size={14} className="text-teal-700" />
                    Sandbox Instance Access
                  </div>
                  <div className="grid grid-cols-2 gap-6">
                    {project.demoCredentials.user && (
                      <div className="flex flex-col gap-1.5">
                        <span className="text-[8px] font-black text-[#0C2C39]/40 uppercase tracking-widest">Identifier</span>
                        <span className="text-sm text-[#0C2C39] font-black tracking-tight truncate">{project.demoCredentials.user}</span>
                      </div>
                    )}
                    {project.demoCredentials.password && (
                      <div className="flex flex-col gap-1.5">
                        <span className="text-[8px] font-black text-[#0C2C39]/40 uppercase tracking-widest">Secret</span>
                        <span className="text-sm text-[#0C2C39] font-black tracking-tight truncate">{project.demoCredentials.password}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <Link to={`/project-details?id=${project.id}`}>
                <Button className="w-full px-10 py-5 bg-[#0C2C39] text-white font-black uppercase tracking-widest text-[10px] rounded-[11px] shadow-[4px_4px_0px_0px_rgba(0,0,0,0.1)] hover:shadow-none transition-all duration-300">
                  Detailed Architecture
                </Button>
              </Link>
            </div>
          </motion.div>
        ))}
      </div>

      {/* View All Button */}
      <div className="mt-20 flex justify-center">
        <Link to="/projects-showcase">
          <Button className="px-12 py-5 bg-white border-4 border-[#0C2C39] text-[#0C2C39] font-black uppercase tracking-widest text-xs rounded-2xl shadow-[8px_8px_0px_0px_rgba(0,0,0,0.1)] hover:shadow-none hover:bg-[#FFF9EB] transition-all duration-300 flex items-center group">
            Explore Full Repository
            <Icon name="ArrowRight" size={16} className="ml-3 group-hover:translate-x-2 transition-transform" />
          </Button>
        </Link>
      </div>
    </div>
  );
};


export default FeaturedProjects;